import React, { useState, useEffect, useCallback } from 'react';
import { 
    Box, Typography, Grid, TextField, MenuItem, 
    Button, Table, TableBody, TableCell, TableContainer, 
    TableHead, TableRow, Paper, CircularProgress, Alert,
    IconButton, Tooltip
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import FilterListIcon from '@mui/icons-material/FilterList';
import { RateService } from '../api/services/rateService';
import { RoomService } from '../api/services/roomService';
import ReplayIcon from '@mui/icons-material/Replay';
import { useCurrency, formatCurrency } from '../utils/currency';

const CHANNELS = ['Direct', 'OTA', 'Corporate'];

export default function RateCalendar() {
    const { currency, currencySymbol } = useCurrency();
    // Filter States
    const [filters, setFilters] = useState({
        startDate: new Date().toISOString().split('T')[0],
        endDate: new Date(new Date().setDate(new Date().getDate() + 7)).toISOString().split('T')[0],
        roomTypeId: '',
        channel: ''
    });

    // Data States
    const [calendarData, setCalendarData] = useState([]);
    const [roomTypes, setRoomTypes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const fetchRoomTypes = useCallback(async () => {
        try {
            const authData = JSON.parse(localStorage.getItem('adminAuth') || '{}');
            const tenantId = authData.tenantId || '1';
            const response = await RoomService.getRoomTypes();
            setRoomTypes(Array.isArray(response) ? response : (Array.isArray(response?.data) ? response.data : []));
        } catch (err) {
            console.error('Failed to fetch room types:', err);
        }
    }, []);

    const fetchCalendarData = useCallback(async () => {
        if (roomTypes.length === 0) return; // Wait for room types
        
        setLoading(true);
        setError(null);
        try {
            const authData = JSON.parse(localStorage.getItem('adminAuth') || '{}');
            const tenantId = authData.tenantId || '1';
            
            let allData = [];
            
            if (filters.roomTypeId) {
                // Fetch for specific room type
                const params = {
                    startDate: new Date(filters.startDate).toISOString(),
                    endDate: new Date(filters.endDate).toISOString(),
                    roomTypeId: filters.roomTypeId
                };
                if (filters.channel) params.channel = filters.channel;
                
                const response = await RateService.getRateCalendar(params, tenantId);
                const data = Array.isArray(response.data) ? response.data : (Array.isArray(response) ? response : []);
                allData = data.map(d => ({ ...d, roomTypeId: filters.roomTypeId }));
            } else {
                // Fetch for all room types to build the grid
                const promises = roomTypes.map(async (rt) => {
                    const params = {
                        startDate: new Date(filters.startDate).toISOString(),
                        endDate: new Date(filters.endDate).toISOString(),
                        roomTypeId: rt.id
                    };
                    if (filters.channel) params.channel = filters.channel;
                    
                    try {
                        const response = await RateService.getRateCalendar(params, tenantId);
                        const data = Array.isArray(response.data) ? response.data : (Array.isArray(response) ? response : []);
                        return data.map(d => ({ ...d, roomTypeId: rt.id }));
                    } catch (e) {
                        console.error(`Failed to fetch for room type ${rt.name}`, e);
                        return [];
                    }
                });
                
                const results = await Promise.all(promises);
                allData = results.flat();
            }

            setCalendarData(allData);
        } catch (err) {
            console.error('Failed to fetch calendar data:', err);
            setError('Failed to load rate calendar. Please check your filters.');
        } finally {
            setLoading(false);
        }
    }, [filters, roomTypes]);

    useEffect(() => {
        fetchRoomTypes();
    }, [fetchRoomTypes]);

    useEffect(() => {
        fetchCalendarData();
    }, [fetchCalendarData]);

    const handleFilterChange = (field, value) => {
        setFilters(prev => ({ ...prev, [field]: value }));
    };

    const getDatesArray = () => {
        const start = new Date(filters.startDate);
        const end = new Date(filters.endDate);
        const dates = [];
        let current = new Date(start);
        
        while (current <= end && dates.length < 31) { // Limit to 31 days for safety
            dates.push(new Date(current).toISOString().split('T')[0]);
            current.setDate(current.getDate() + 1);
        }
        return dates;
    };

    const dates = getDatesArray();

    // Group data by room type for display
    const organizedData = roomTypes
        .filter(rt => !filters.roomTypeId || rt.id === filters.roomTypeId)
        .map(rt => {
            const row = { roomType: rt.name, id: rt.id, dates: {} };
            dates.forEach(date => {
                const entry = calendarData.find(d => 
                    d.roomTypeId === rt.id && 
                    d.date && d.date.substring(0, 10) === date
                );
                
                let rateValue = rt.baseRate;
                let planName = null;
                let planType = null;
                
                if (entry) {
                    if (entry.effectiveRate !== undefined && entry.effectiveRate !== 0) {
                        rateValue = entry.effectiveRate;
                    } else if (entry.rate !== undefined && entry.rate !== 0) {
                        rateValue = entry.rate;
                    }
                    
                    if (entry.planName && entry.planName !== 'N/A') {
                        planName = entry.planName;
                        planType = entry.planType;
                    }
                }
                
                row.dates[date] = {
                    rate: rateValue,
                    planName: planName,
                    planType: planType
                };
            });
            return row;
        });

    return (
        <Box>
            {/* Filter Bar */}
            <Paper sx={{ p: 2, mb: 3, borderRadius: 2, boxShadow: '0 2px 10px rgba(0,0,0,0.05)' }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={6} md={2.5}>
                        <TextField
                            fullWidth
                            label="Start Date"
                            type="date"
                            value={filters.startDate}
                            onChange={(e) => handleFilterChange('startDate', e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            size="small"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={2.5}>
                        <TextField
                            fullWidth
                            label="End Date"
                            type="date"
                            value={filters.endDate}
                            onChange={(e) => handleFilterChange('endDate', e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            size="small"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={2.5}>
                        <TextField
                            select
                            fullWidth
                            label="Room Type"
                            value={filters.roomTypeId}
                            onChange={(e) => handleFilterChange('roomTypeId', e.target.value)}
                            size="small"
                        >
                            <MenuItem value="">All Room Types</MenuItem>
                            {roomTypes.map(rt => (
                                <MenuItem key={rt.id} value={rt.id}>{rt.name}</MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2.5}>
                        <TextField
                            select
                            fullWidth
                            label="Channel"
                            value={filters.channel}
                            onChange={(e) => handleFilterChange('channel', e.target.value)}
                            size="small"
                        >
                            <MenuItem value="">All Channels</MenuItem>
                            {CHANNELS.map(c => (
                                <MenuItem key={c} value={c}>{c}</MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item xs={12} md={2} sx={{ display: 'flex', gap: 1 }}>
                        {(filters.roomTypeId !== '' || filters.channel !== '') && (
                            <Button
                                variant="outlined"
                                startIcon={<ReplayIcon sx={{ fontSize: 16 }} />}
                                sx={{ textTransform: 'none', px: 2 }}
                                onClick={() => {
                                    setFilters({
                                        startDate: new Date().toISOString().split('T')[0],
                                        endDate: new Date(new Date().setDate(new Date().getDate() + 7)).toISOString().split('T')[0],
                                        roomTypeId: '',
                                        channel: ''
                                    });
                                }}
                                disabled={loading}
                            >
                                Reset
                            </Button>
                        )}
                        <Tooltip title="Refresh">
                            <IconButton onClick={fetchCalendarData} disabled={loading}>
                                <RefreshIcon />
                            </IconButton>
                        </Tooltip>
                    </Grid>
                </Grid>
            </Paper>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {/* Calendar Grid */}
            <TableContainer component={Paper} sx={{ position: 'relative' }}>
                {loading && (
                    <Box sx={{ 
                        position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, 
                        display: 'flex', justifyContent: 'center', alignItems: 'center', 
                        bgcolor: 'rgba(255,255,255,0.7)', zIndex: 1 
                    }}>
                        <CircularProgress />
                    </Box>
                )}
                <Table size="small" stickyHeader>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ minWidth: 150, bgcolor: '#f8fafc' }}>Room Type</TableCell>
                            {dates.map(date => (
                                <TableCell key={date} align="center" sx={{ minWidth: 80, bgcolor: '#f8fafc' }}>
                                    {new Date(date).toLocaleDateString('en-US', { weekday: 'short', day: 'numeric', month: 'short' })}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {organizedData.length > 0 ? (
                            organizedData.map((row) => (
                                <TableRow key={row.id} hover>
                                    <TableCell sx={{ fontWeight: 'medium' }}>{row.roomType}</TableCell>
                                    {dates.map(date => {
                                        const cell = row.dates[date];
                                        const hasPlan = !!cell.planName;
                                        
                                        let bgColor = 'primary.light'; // Base rate (default)
                                        if (hasPlan) {
                                            if (cell.planType === 'Seasonal') bgColor = 'warning.main'; // Amber
                                            else if (cell.planType === 'Promotional') bgColor = 'success.main'; // Green
                                            else if (cell.planType === 'Corporate') bgColor = 'info.main'; // Blue
                                            else bgColor = 'secondary.main'; // Fallback for other plans
                                        }
                                        
                                        return (
                                            <TableCell key={date} align="center">
                                                <Tooltip title={hasPlan ? `${cell.planType} Plan: ${cell.planName}` : 'Base Rate'}>
                                                    <Box sx={{ 
                                                        p: 1.5, 
                                                        borderRadius: 1, 
                                                        bgcolor: bgColor,
                                                        color: 'white',
                                                        fontWeight: 'bold',
                                                        fontSize: '0.875rem',
                                                        cursor: 'help',
                                                        transition: 'transform 0.2s',
                                                        '&:hover': { transform: 'scale(1.05)' }
                                                    }}>
                                                        {currencySymbol === '₹' ? `₹${cell.rate}` : `${currencySymbol} ${cell.rate}`}
                                                    </Box>
                                                </Tooltip>
                                            </TableCell>
                                        );
                                    })}
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={dates.length + 1} align="center" sx={{ py: 8 }}>
                                    <Typography color="text.secondary">No data available for the selected range.</Typography>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
}
